/**
 * PasswordField -- Dynamic password input field.
 *
 * Renders a password input with a show/hide toggle button. Uses the shadcn/ui
 * Input and Button components with lucide-react Eye/EyeOff icons.  Follows
 * the same accessibility pattern as the existing PasswordInput component in
 * `components/connectors/shared/PasswordInput.tsx`.
 */

import { useState, useCallback } from 'react';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PASSWORD_MANAGER_OPT_OUT } from '@/components/connectors/shared/passwordManagerOptOut';
import type { DynamicFieldProps } from './index';

export function PasswordField({ field, value, onChange, error, readOnly, hasStoredValue }: DynamicFieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  const inputId = `field-${field.name}`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const toggleVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={inputId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className="relative">
        <Input
          id={inputId}
          type={showPassword ? 'text' : 'password'}
          {...PASSWORD_MANAGER_OPT_OUT}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hasStoredValue && !value ? '••••••••••••  (stored)' : (field.placeholder ?? undefined)}
          disabled={readOnly}
          maxLength={field.max_length ?? undefined}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(
            'pr-10',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
        />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={toggleVisibility}
          disabled={readOnly}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </Button>
      </div>

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
